import React, { useState, useEffect, useCallback } from "react";
import ParagraphTag from "../../reusebale/Paragraph";
import ReactPaginate from "react-paginate";
import NoData from "../../reusebale/noData";
import LeaseTableLoader from "../../skeleton-loader/lease-table";
import moment from "moment";
import { ArrowUpDown, ArrowDownUp } from "lucide-react";
import BaseUrl from "../../../constants/BaseUrl";
import { useSelector } from "react-redux";

const SubscriptionPayments = () => {
  const { loginSuccess } = useSelector((state) => state.auth);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOrder, setSortOrder] = useState("desc");
  const itemsPerPage = 10;

  const fetchSubscriptions = useCallback(() => {
    setLoading(true);
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");
    myHeaders.append("Authorization", `Bearer ${loginSuccess?.token}`);

    fetch(`${BaseUrl}landlord/profile/getAllSubscriptionsAdmin`, {
      method: "GET",
      headers: myHeaders,
    })
      .then((r) => r.json())
      .then((result) => {
        if (result.status === "success") setSubscriptions(result.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [loginSuccess?.token]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const toggleSort = () => setSortOrder((p) => (p === "desc" ? "asc" : "desc"));

  const sorted = [...subscriptions].sort((a, b) => {
    const diff = new Date(a.createdAt) - new Date(b.createdAt);
    return sortOrder === "desc" ? -diff : diff;
  });

  const pageCount = Math.ceil(sorted.length / itemsPerPage);
  const currentItems = sorted.slice(
    currentPage * itemsPerPage,
    currentPage * itemsPerPage + itemsPerPage
  );

  const getStatusBadge = (status) => {
    const base = "px-2 py-0.5 rounded-full text-xs font-popinsMedium";
    if (status === "active")
      return <span className={`${base} bg-green-100 text-green-700`}>Active</span>;
    if (status === "invalid")
      return <span className={`${base} bg-red-100 text-red-700`}>Invalid</span>;
    if (status === "pending")
      return <span className={`${base} bg-yellow-100 text-yellow-700`}>Pending</span>;
    if (status === "inactive")
      return <span className={`${base} bg-gray-100 text-gray-500`}>Inactive</span>;
    if (status === "expired")
      return <span className={`${base} bg-orange-100 text-orange-600`}>Expired</span>;
    return <span className={`${base} bg-gray-100 text-gray-500`}>{status}</span>;
  };

  return (
    <div className="bg-white rounded-2xl py-5 px-4 w-full mt-5 shadow-md shadow-gray-200">
      <div className="flex justify-between items-center mb-4">
        <div>
          <ParagraphTag
            content="Subscription Payments"
            classes="text-darkColor font-popinsSemiBold text-base"
          />
          <ParagraphTag
            content={`${subscriptions.length} total paid records`}
            classes="text-darkColor/40 font-popinsRegular text-xs mt-0.5"
          />
        </div>
      </div>

      {/* Header */}
      <div className="flex border-b border-darkColor/20 pb-2">
        <div className="w-[22%]">
          <p className="text-darkColor/40 font-popinsMedium text-xs">Landlord</p>
        </div>
        <div className="w-[20%]">
          <p className="text-darkColor/40 font-popinsMedium text-xs">Plan</p>
        </div>
        <div className="w-[11%] text-center">
          <p className="text-darkColor/40 font-popinsMedium text-xs">Invoice</p>
        </div>
        <div className="w-[10%] text-center">
          <p className="text-darkColor/40 font-popinsMedium text-xs">Amount</p>
        </div>
        <div className="w-[10%] text-center">
          <p className="text-darkColor/40 font-popinsMedium text-xs">Billing</p>
        </div>
        <div className="w-[11%] text-center">
          <p className="text-darkColor/40 font-popinsMedium text-xs">Status</p>
        </div>
        <div
          className="w-[8%] flex items-center justify-end gap-1 cursor-pointer"
          onClick={toggleSort}
        >
          <p className="text-darkColor/40 font-popinsMedium text-xs">Date</p>
          {sortOrder === "desc" ? (
            <ArrowDownUp size={12} className="text-darkColor/40" />
          ) : (
            <ArrowUpDown size={12} className="text-darkColor/40" />
          )}
        </div>
        <div className="w-[8%] text-end">
          <p className="text-darkColor/40 font-popinsMedium text-xs">End Date</p>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <LeaseTableLoader />
      ) : currentItems.length === 0 ? (
        <NoData />
      ) : (
        currentItems.map((item, i) => (
          <div
            key={item._id || i}
            className="flex items-center py-2.5 border-b border-gray-50"
          >
            <div className="w-[22%] pr-2">
              <p className="text-darkColor font-popinsSemiBold text-sm truncate">
                {item.landlordInfo
                  ? `${item.landlordInfo.firstName} ${item.landlordInfo.lastName}`
                  : "—"}
              </p>
              <p className="text-darkColor/40 font-popinsRegular text-xs truncate">
                {item.landlordInfo?.email || ""}
              </p>
            </div>
            <div className="w-[20%] pr-2">
              <p className="text-themeColor font-popinsMedium text-xs leading-tight">
                {item.subscriptionPlanName || "—"}
              </p>
            </div>
            <div className="w-[11%] text-center">
              <p className="text-darkColor/60 font-popinsMedium text-xs">
                {["active", "expired"].includes(item.status) && item._id
                  ? `INV-${item._id.toString().slice(-8)}`
                  : "—"}
              </p>
            </div>
            <div className="w-[10%] text-center">
              <p className="text-darkColor font-popinsMedium text-sm">
                ${Number(item.amount || 0).toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </p>
            </div>
            <div className="w-[10%] text-center">
              <p className="text-darkColor/60 font-popinsRegular text-xs capitalize">
                {item.subscriptionType || "—"}
              </p>
            </div>
            <div className="w-[11%] flex justify-center">
              {getStatusBadge(item.status)}
            </div>
            <div className="w-[8%] text-end">
              <p className="text-darkColor/60 font-popinsRegular text-xs">
                {item.createdAt ? moment(item.createdAt).format("MM/DD/YY") : "—"}
              </p>
            </div>
            <div className="w-[8%] text-end">
              <p className="text-darkColor/60 font-popinsRegular text-xs">
                {item.endDate ? moment(item.endDate).format("MM/DD/YY") : "—"}
              </p>
            </div>
          </div>
        ))
      )}

      {/* Pagination */}
      {!loading && pageCount > 1 && (
        <div className="flex justify-end mt-4">
          <ReactPaginate
            previousLabel={"←"}
            nextLabel={"→"}
            breakLabel={"..."}
            pageCount={pageCount}
            marginPagesDisplayed={2}
            pageRangeDisplayed={3}
            onPageChange={({ selected }) => setCurrentPage(selected)}
            forcePage={currentPage}
            containerClassName={"flex items-center gap-1"}
            pageLinkClassName={
              "px-2.5 py-1 rounded border border-gray-200 text-xs font-popinsMedium text-darkColor/60 hover:bg-gray-50"
            }
            activeLinkClassName={"!bg-themeColor !text-white !border-themeColor"}
            previousLinkClassName={
              "px-2.5 py-1 rounded border border-gray-200 text-xs font-popinsMedium text-darkColor/60 hover:bg-gray-50"
            }
            nextLinkClassName={
              "px-2.5 py-1 rounded border border-gray-200 text-xs font-popinsMedium text-darkColor/60 hover:bg-gray-50"
            }
            breakClassName={"text-xs text-darkColor/40"}
          />
        </div>
      )}
    </div>
  );
};

export default SubscriptionPayments;
